import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DataTable,
    type DataTableColumnDef,
    type DataTableRowVariant,
} from '@/components/organisms/DataTable';
import { hasWoundScoringPen, missingWoundsNoteKey } from '@/domain/cages/speciesTraits';
import type { LocationsLicenseOverview, LocationType, OverviewCageStatus } from '@/domain/client';
import type { MarkHistoricalInput } from '@/domain/locations';
import { useHealthModuleEnabled } from '@/hooks/useHealthModuleEnabled';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import {
    getOverviewHealthColumnVisibility,
    isOverviewBwPen,
    isOverviewColorPen,
} from '@/routes/Client/Cage/pages/Health/_utils/overviewHealthColumnVisibility';

import LocationOverviewTableFooter from './LocationOverviewTableFooter';
import { type OverviewCellContext, renderCageCell } from './overviewCageCells';
import { renderCohortCell } from './overviewCohortCells';
import { penLabelKey } from './penLabel';
import { isCageRow, type LocationOverviewRow } from './types';
import { useHistoricalSelection } from './useHistoricalSelection';
import { useLocationOverviewColumns } from './useLocationOverviewColumns';

import styles from './LocationOverviewTable.module.scss';

/**
 * Matches the legacy row height, which came out of the small table-variant link
 * button (30px) plus the table's cell padding. Verify against the app before changing.
 */
const ROW_HEIGHT = 38;

/** An empty pen reads like an inactive one: a pen, but nothing in production under a bioscope. */
const ROW_VARIANT: Record<OverviewCageStatus, DataTableRowVariant> = {
    active: 'default',
    inactive: 'inactive',
    empty: 'inactive',
    historical: 'historical',
};

export interface LocationOverviewTableProps {
    /** Active, inactive, empty, then historical, already sorted. Build with `useLocationOverviewRows`. */
    rows: LocationOverviewRow[];
    clientId: number;
    locationId: number;
    locationType: LocationType;
    locationLicense?: LocationsLicenseOverview;
    isOptoScaleAdmin?: boolean;
    hasAccessToMarkHistorical: boolean;
    /**
     * Saves the confirmed batch of historical changes, and resolves once the overview shows
     * them. Marking a pen historical also closes the fish group it holds.
     */
    markHistorical: (changes: MarkHistoricalInput[]) => Promise<unknown>;
    /** Hides the show-historical / mark-historical actions (region overview). */
    hideMarkHistorical?: boolean;
    /** Drops rows without a weight measurement and shows the hidden-pens note. */
    hideNoMeasurements?: boolean;
    /** Drops inactive rows. */
    hideInactive?: boolean;
}

export default function LocationOverviewTable(props: LocationOverviewTableProps) {
    const {
        rows,
        clientId,
        locationId,
        locationType,
        locationLicense,
        isOptoScaleAdmin = false,
        hasAccessToMarkHistorical,
        markHistorical,
        hideMarkHistorical = false,
        hideNoMeasurements = false,
        hideInactive = false,
    } = props;

    const { t } = useTranslation();
    const [showHistorical, setShowHistorical] = useState(false);
    const [showMarkHistorical, setShowMarkHistorical] = useState(false);
    const closeMarkHistorical = useCallback(() => setShowMarkHistorical(false), []);
    const historical = useHistoricalSelection(locationId, markHistorical, closeMarkHistorical);

    const licenses = locationLicense?.licenses;
    const hasLicense = (hasValidLicense?: boolean) =>
        isOptoScaleAdmin || (hasValidLicense ?? false);

    const hasAccessToFishHealth = hasLicense(licenses?.fishHealthLicense.hasValidLicense);
    const hasAccessToLice = hasLicense(licenses?.liceLicense.hasValidLicense);
    const hasAccessToLaksvel = hasLicense(licenses?.laksvelLicense.hasValidLicense);
    const hasAccessToFishSpeed = hasLicense(licenses?.fishSpeedLicense.hasValidLicense);
    const hasAccessToBehavior = hasLicense(licenses?.behaviorLicense.hasValidLicense);

    const isHealthModuleEnabled = useHealthModuleEnabled(hasAccessToFishHealth);
    const isBehaviorModuleEnabled = hasAccessToBehavior;

    // Read here rather than taken as a prop, matching `useHealthModuleEnabled`
    // above: both Region Overview and Location Dashboard render this table, so
    // gating it once here is what keeps the two views identical. The containers
    // already ask the API for the data behind the same flag, so the column and its
    // contents appear together.
    const { enabled: isFishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');

    /**
     * The pens every column gate below is allowed to look at.
     *
     * Derived once and shared: both gates need the identical list, and computing it
     * twice is what let the two drift apart — the wound gate arrived without the
     * `isCageRow` filter and crashed on `undefined.fishType`.
     *
     * Two exclusions, for different reasons. Historical pens are out as in the
     * legacy table: a historical colour pen must not switch a health column on.
     * Departed-cohort rows are out because they are not pens at all — they carry no
     * cage, and every helper here reads cage fields.
     */
    const gatedCages = useMemo(
        () =>
            rows
                .filter(isCageRow)
                .filter((row) => row.status !== 'historical')
                .map((row) => row.cage),
        [rows]
    );

    // Species comes from the pens rather than the location: a location can hold
    // several (WB-2207), so one salmon pen is enough to keep the wound column.
    const { woundScoringPen, missingWoundsNote } = useMemo(
        () => ({
            woundScoringPen: hasWoundScoringPen(gatedCages),
            missingWoundsNote: missingWoundsNoteKey(gatedCages),
        }),
        [gatedCages]
    );

    const healthColumns = useMemo(() => {
        return getOverviewHealthColumnVisibility({
            isHealthModuleEnabled,
            hasAccessToLaksvel,
            hasAccessToFishHealth,
            hasWoundScoringPen: woundScoringPen,
            hasBwPen: gatedCages.some((cage) => isOverviewBwPen(cage)),
            hasColorPen: gatedCages.some((cage) => isOverviewColorPen(cage)),
        });
    }, [
        gatedCages,
        isHealthModuleEnabled,
        hasAccessToLaksvel,
        hasAccessToFishHealth,
        woundScoringPen,
    ]);

    const { columns, visibility } = useLocationOverviewColumns({
        locationType,
        hasAccessToLice,
        hasAccessToFishSpeed,
        hasAccessToBehavior,
        showBetaLaksvelColumn: healthColumns.showBetaLaksvelColumn,
        showWoundColumn: healthColumns.showWoundColumn,
        showMarkHistorical,
        showFishGroupColumn: isFishGroupsEnabled,
    });

    // Order matters: the legacy CSS let `hide-no-measurements` win over `show-all`.
    const visibleRows = useMemo(
        () =>
            rows.filter((row) => {
                // Departed cohorts share the historical toggle. Without a gate a
                // static cage would pile them up indefinitely and bury the pens
                // that are actually in production; grouping them with the other
                // past rows is also where a reader expects to find them.
                if (row.status === 'closedFishGroup') {
                    return showHistorical && !hideNoMeasurements;
                }
                if (hideNoMeasurements && row.cage.cageData.weight == null) return false;
                // Empty pens go with the inactive ones: neither has fish under a bioscope.
                if (hideInactive && (row.status === 'inactive' || row.status === 'empty')) {
                    return false;
                }
                if (row.status === 'historical' && !showHistorical) return false;
                return true;
            }),
        [rows, hideNoMeasurements, hideInactive, showHistorical]
    );

    // `closedFishGroup` rows count too: they are what the toggle now reveals, so a
    // location whose only past rows are departed cohorts still gets the control.
    const hasHistoricalRows = rows.some(
        (row) => row.status === 'historical' || row.status === 'closedFishGroup'
    );
    const hasHiddenPens = rows.some(
        (row) =>
            isCageRow(row) &&
            (row.status === 'inactive' ||
                row.status === 'empty' ||
                row.cage.cageData.weight == null)
    );

    const cellContext: OverviewCellContext = {
        clientId,
        locationId,
        locationType,
        isHealthModuleEnabled,
        isBehaviorModuleEnabled,
        historicalSelection: historical.selection,
        showsNameColumn: visibility.name,
        t,
    };
    const tableColumns: DataTableColumnDef<LocationOverviewRow>[] = columns.map((column) => ({
        id: column.key,
        header: column.header,
        meta: {
            width: column.width,
            // The pen column is headerless on screen while it only holds "Open" buttons.
            headerScreenReaderText:
                column.key === 'cage' && column.header === ''
                    ? t(penLabelKey(locationType))
                    : undefined,
            cellTitle:
                column.key === 'name'
                    ? ({ original }) =>
                          isCageRow(original)
                              ? `${t(penLabelKey(locationType))} ${original.cage.cageName}`
                              : undefined
                    : undefined,
            cellAlign:
                column.key === 'laksvel'
                    ? ({ original }) => (isCageRow(original) ? 'center' : 'left')
                    : undefined,
        },
        cell: ({ row: { original } }) =>
            isCageRow(original)
                ? renderCageCell(column.key, original, cellContext)
                : renderCohortCell(column.key, original, cellContext),
    }));

    return (
        <DataTable
            data={visibleRows}
            columns={tableColumns}
            getRowId={rowKey}
            layout="grid"
            enableSorting={false}
            rowHeight={ROW_HEIGHT}
            className={styles.table}
            getRowProps={({ original }) => ({
                variant: isCageRow(original) ? ROW_VARIANT[original.status] : 'historical',
            })}
            footer={
                <LocationOverviewTableFooter
                    clientId={clientId}
                    locationId={locationId}
                    locationType={locationType}
                    missingWoundsNoteKey={missingWoundsNote}
                    showHiddenPensNote={hideNoMeasurements && hasHiddenPens}
                    showHistoricalActions={!hideMarkHistorical}
                    hasHistoricalRows={hasHistoricalRows}
                    showHistorical={showHistorical}
                    onToggleShowHistorical={() => setShowHistorical(!showHistorical)}
                    hasAccessToMarkHistorical={hasAccessToMarkHistorical}
                    showMarkHistorical={showMarkHistorical}
                    onToggleMarkHistorical={() => {
                        // Leaving tag mode drops the unconfirmed ticks.
                        historical.reset();
                        setShowMarkHistorical(!showMarkHistorical);
                    }}
                    pendingHistoricalCount={historical.count}
                    isSubmittingHistorical={historical.selection.isSubmitting}
                    onConfirmHistorical={historical.confirm}
                />
            }
        />
    );
}

/**
 * A cohort can have lived in the same cage twice, so neither id alone is unique — the
 * pair, with the date it arrived, is.
 */
function rowKey(row: LocationOverviewRow): string {
    return row.status === 'closedFishGroup'
        ? `fg-${row.closedFishGroup.fishGroupId}-${row.closedFishGroup.cageId}-${row.closedFishGroup.fromDate}`
        : String(row.cage.cageId);
}
